import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser, sortCampaignsByCreatedAt } from '../userContext';
import { clearAllStatsCache, fetchMultipleGameStats, fetchMultipleGameDailyStats, getTodayStatsForMultipleCampaigns } from '../services/gameAnalytics';
import { sumMetricLastNDays, buildSparkline, sumMetricSince, formatSinceLabel } from '../utils/dashboardAggregator';
import { normalizeCampaignStatus } from '../data/campaignStatus';
import { gameTypes } from '../data/GameTypes';
import { getGameTypeColors } from '../utils/gameTypeColors';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import CampaignsCard from '../partials/campaigns/CampaignsCard';
import { auth } from '../firebase';
import { useCreditModal } from '../components/CreditModalContext';
import ProceduralBackground from '../games/ProceduralBackground';

function DashboardSkeleton() {
  return (
    <div className="gch-content-width gch-page">
      <div className="mb-8">
        <div className="w-48 h-10 gch-card animate-pulse" style={{ height: 40 }} />
      </div>
      <div className="mb-8">
        <div className="gch-card p-6">
          <div className="space-y-4">
            <div className="h-6 w-1/4 rounded animate-pulse bg-white/10" />
            <div className="h-[300px] rounded animate-pulse bg-white/5 gch-surface" />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mb-6">
        <div className="w-24 h-10 rounded animate-pulse bg-white/10" />
      </div>
      <div className="grid grid-cols-12 gap-6">
        {[1, 2, 3].map((index) => (
          <div key={index} className="col-span-12 sm:col-span-6 lg:col-span-4">
            <div className="gch-card p-5">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="w-32 h-4 rounded animate-pulse bg-white/10" />
                  <div className="w-8 h-8 rounded-full animate-pulse bg-white/10" />
                </div>
                <div className="w-3/4 h-6 rounded animate-pulse bg-white/10" />
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-16 rounded animate-pulse bg-white/5" />
                  <div className="h-16 rounded animate-pulse bg-white/5" />
                </div>
                <div className="flex justify-between items-center">
                  <div className="w-20 h-4 rounded animate-pulse bg-white/10" />
                  <div className="w-24 h-4 rounded animate-pulse bg-white/10" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard() {
  const { user, userProfile, businessProfile, campaigns: contextCampaigns, campaignsReady, gameCredits, canPublish } = useUser();
  const [games, setGames] = useState([]);
  const navigate = useNavigate();
  const { openCreditModal } = useCreditModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [gameIds, setGameIds] = useState([]);
  const [playCountsMap, setPlayCountsMap] = useState({}); // gameId -> { totalPlays, ... }
  const [todayStatsMap, setTodayStatsMap] = useState({}); // gameId -> { plays, shares, ctas }
  const [dailyStatsMap, setDailyStatsMap] = useState({}); // gameId -> daily stats array (last 7 days)
  // "Since you last visited": read once from localStorage on first mount.
  // We capture the previous value into state, then immediately overwrite the
  // stored value with today so the next visit gets a fresh comparison window.
  // useEffect (not render-body assignment) keeps this StrictMode-safe in dev.
  const [lastVisitIsoDate, setLastVisitIsoDate] = useState('');
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const stored = window.localStorage.getItem('sf:lastDashboardVisit');
      const todayIso = new Date().toISOString().split('T')[0];
      if (stored && stored < todayIso) setLastVisitIsoDate(stored);
      window.localStorage.setItem('sf:lastDashboardVisit', todayIso);
    } catch {
      // Ignore storage failures (private mode, etc.) - strip just won't show.
    }
  }, []);
  const [showUpgradedBanner, setShowUpgradedBanner] = useState(false);
  const [upgradeChargeLabel, setUpgradeChargeLabel] = useState(null);
  const [showCreditSuccessBanner, setShowCreditSuccessBanner] = useState(false);
  const [showCreditCancelBanner, setShowCreditCancelBanner] = useState(false);

  // After plan upgrade, user is redirected here with ?upgraded=agency - show success and clean URL
  useEffect(() => {
    if (['agency', 'team'].includes(searchParams.get('upgraded')) || ['agency', 'team'].includes(searchParams.get('switched'))) {
      // Extract charge amount if available
      const charged = searchParams.get('charged');
      const cur = searchParams.get('cur') || 'gbp';
      if (charged && Number(charged) > 0) {
        const symbol = cur === 'usd' ? '$' : '£';
        setUpgradeChargeLabel(`${symbol}${(Number(charged) / 100).toFixed(2)}`);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('upgraded');
      next.delete('switched');
      next.delete('charged');
      next.delete('cur');
      setSearchParams(next, { replace: true });
      setShowUpgradedBanner(true);
      const t = setTimeout(() => setShowUpgradedBanner(false), 8000);
      return () => clearTimeout(t);
    }
  }, [searchParams, setSearchParams]);

  // Current credit-pack checkout flow: Stripe redirects here with
  // ?credit_purchase=success&session_id=… when the user bought credits
  // without a specific campaign in flight. The webhook has already granted
  // the credit server-side, so we just confirm + clean the URL.
  useEffect(() => {
    const status = searchParams.get('credit_purchase');
    if (status !== 'success' && status !== 'cancel') return;
    const next = new URLSearchParams(searchParams);
    next.delete('credit_purchase');
    next.delete('session_id');
    setSearchParams(next, { replace: true });
    if (status === 'success') setShowCreditSuccessBanner(true);
    else setShowCreditCancelBanner(true);
  }, [searchParams, setSearchParams]);

  // Legacy fallback for old publish-credit checkout redirects.
  useEffect(() => {
    const sessionId = searchParams.get('credit_session');
    if (!sessionId || !user?.uid) return;
    const next = new URLSearchParams(searchParams);
    next.delete('credit_session');
    setSearchParams(next, { replace: true });
    auth.currentUser.getIdToken().then(token =>
    fetch(`${process.env.REACT_APP_API_BASE_URL}/app/verify-game-credit-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ sessionId }),
    }))
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setShowCreditSuccessBanner(true);
        else console.error('[verify-game-credit-session] unexpected response:', data);
      })
      .catch((err) => console.error('[verify-game-credit-session] fetch error:', err));
  }, [searchParams, setSearchParams, user]);

  // Sync games from UserContext (single source of truth). We wait for
  // `campaignsReady` before flipping out of loading state — otherwise an
  // empty initial array can briefly read as "new user" and flash the
  // first-time hero banner over a returning user.
  useEffect(() => {
    if (campaignsReady && Array.isArray(contextCampaigns)) {
      const sortedGames = sortCampaignsByCreatedAt(contextCampaigns);
      setGames(sortedGames);
      setGameIds(sortedGames.map(game => game.id));
      setIsLoading(false);
    } else if (user && businessProfile?.id) {
      // Context not ready yet - keep loading
      setIsLoading(true);
    }
  }, [contextCampaigns, campaignsReady, user, businessProfile]);

  const gameIdsRef = useRef(gameIds);
  gameIdsRef.current = gameIds;

  const refetchDashboardStats = useCallback(() => {
    const ids = gameIdsRef.current;
    if (ids.length === 0) return;
    Promise.all([
      fetchMultipleGameStats(ids),
      getTodayStatsForMultipleCampaigns(ids),
      fetchMultipleGameDailyStats(ids, 30) // 30-day window covers both KPI sparkline and since-last-visit delta
    ]).then(([playMap, todayMap, dailyMap]) => {
      setPlayCountsMap(playMap);
      setTodayStatsMap(todayMap);
      setDailyStatsMap(dailyMap);
    });
  }, []);

  // Fetch play counts and today's stats for dashboard
  useEffect(() => {
    if (gameIds.length === 0) return;
    let cancelled = false;
    Promise.all([
      fetchMultipleGameStats(gameIds),
      getTodayStatsForMultipleCampaigns(gameIds),
      fetchMultipleGameDailyStats(gameIds, 30)
    ]).then(([playMap, todayMap, dailyMap]) => {
      if (!cancelled) {
        setPlayCountsMap(playMap);
        setTodayStatsMap(todayMap);
        setDailyStatsMap(dailyMap);
      }
    });
    return () => { cancelled = true; };
  }, [gameIds.join(',')]);

  // Refetch when tab becomes visible (fixes "0 today" when navigating back via browser back)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && gameIdsRef.current.length > 0) {
        refetchDashboardStats();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [refetchDashboardStats]);

  const activeGamesCount = games.length;

  // Last-updated timestamp for sorting (lastEditedAt from editor, or createdAt)
  const campaignLastUpdatedMs = (c) => {
    const edited = c?.lastEditedAt;
    if (edited) return new Date(edited).getTime();
    const created = c?.createdAt;
    if (!created) return 0;
    if (typeof created?.toMillis === 'function') return created.toMillis();
    if (typeof created?.seconds === 'number') return created.seconds * 1000;
    return new Date(created).getTime();
  };

  // Published: sort by plays today desc, then total plays
  const liveGames = useMemo(() => {
    return games
      .filter((g) => normalizeCampaignStatus(g?.status) === 'published')
      .sort((a, b) => {
        const todayA = todayStatsMap[a.id]?.plays ?? 0;
        const todayB = todayStatsMap[b.id]?.plays ?? 0;
        if (todayB !== todayA) return todayB - todayA;
        return (playCountsMap[b.id]?.totalPlays ?? 0) - (playCountsMap[a.id]?.totalPlays ?? 0);
      });
  }, [games, playCountsMap, todayStatsMap]);

  // Total plays today across all dashboard games (same source as Published list)
  const knownTodayPlaysFromList = useMemo(() => {
    return (gameIds || []).reduce((sum, id) => sum + (todayStatsMap[id]?.plays ?? 0), 0);
  }, [gameIds, todayStatsMap]);

  // Aggregate KPIs across every campaign. Headline numbers are last-7-days
  // (the actionable window - "is my stuff working *now*?"). Lifetime totals
  // are kept as a subtitle for context. Sparklines come from the same daily
  // series so they don't trigger a second fetch.
  const aggregateKpis = useMemo(() => {
    const ids = gameIds || [];
    let lifetimePlays = 0, lifetimeCompletions = 0, lifetimeCtas = 0, lifetimeEmails = 0;
    ids.forEach((id) => {
      const s = playCountsMap[id] || {};
      lifetimePlays += s.totalPlays || 0;
      lifetimeCompletions += s.totalCompletions || 0;
      lifetimeCtas += s.totalCtas || 0;
      lifetimeEmails += s.totalEmailCaptures || 0;
    });

    return {
      // Last 7 days (headline)
      plays7d: sumMetricLastNDays(dailyStatsMap, 'plays', 7),
      completions7d: sumMetricLastNDays(dailyStatsMap, 'completions', 7),
      ctas7d: sumMetricLastNDays(dailyStatsMap, 'ctas', 7),
      emails7d: sumMetricLastNDays(dailyStatsMap, 'emailCaptures', 7),
      // Sparklines
      playsSpark: buildSparkline(dailyStatsMap, 'plays', 7),
      completionsSpark: buildSparkline(dailyStatsMap, 'completions', 7),
      ctasSpark: buildSparkline(dailyStatsMap, 'ctas', 7),
      emailsSpark: buildSparkline(dailyStatsMap, 'emailCaptures', 7),
      // Lifetime (subtitle)
      lifetimePlays,
      lifetimeCompletions,
      lifetimeCtas,
      lifetimeEmails,
    };
  }, [gameIds, playCountsMap, dailyStatsMap]);

  // "Since you last visited" deltas. Hidden when there's no prior visit on
  // record (first-time user) or when nothing changed since then.
  const sinceLastVisit = useMemo(() => {
    if (!lastVisitIsoDate) return null;
    const plays = sumMetricSince(dailyStatsMap, 'plays', lastVisitIsoDate);
    const ctas = sumMetricSince(dailyStatsMap, 'ctas', lastVisitIsoDate);
    const emails = sumMetricSince(dailyStatsMap, 'emailCaptures', lastVisitIsoDate);
    if (!plays && !ctas && !emails) return null;
    return {
      since: lastVisitIsoDate,
      label: formatSinceLabel(lastVisitIsoDate),
      plays: plays || 0,
      ctas: ctas || 0,
      emails: emails || 0,
    };
  }, [dailyStatsMap, lastVisitIsoDate]);

  // Pick up where you left off: draft (not yet published), sorted by last updated desc, top 3
  const pickupGames = useMemo(() => {
    return games
      .filter((g) => normalizeCampaignStatus(g?.status) === 'draft')
      .sort((a, b) => campaignLastUpdatedMs(b) - campaignLastUpdatedMs(a))
      .slice(0, 3);
  }, [games]);

  // Combined campaign cards for the dashboard grid: published (sorted by today's
  // plays, then lifetime) first, then drafts (most recently edited). Capped at 6
  // so the dashboard stays scannable; users hit "Show all" for the full list.
  const dashboardCampaignCards = useMemo(() => {
    const drafts = games
      .filter((g) => normalizeCampaignStatus(g?.status) === 'draft')
      .sort((a, b) => campaignLastUpdatedMs(b) - campaignLastUpdatedMs(a));
    return [...liveGames, ...drafts].slice(0, 6);
  }, [games, liveGames]);
  const totalGamesCount = contextCampaigns?.length || 0;
  
  // Onboarding progress tracking (3 steps)
  const hasCreatedGame = totalGamesCount >= 1;
  const hasCustomizedBranding = contextCampaigns?.some(game => 
    game.editedInEditor === true
  ) || false;
  const hasTestedGame = contextCampaigns?.some(game => 
    game.hasBeenPlayed === true
  ) || false;
  
  const completedSteps = [hasCreatedGame, hasCustomizedBranding, hasTestedGame].filter(Boolean).length;
  const onboardingProgress = (completedSteps / 3) * 100;
  
  const hasPublishedGame = games?.some((g) => normalizeCampaignStatus(g?.status) === 'published') ?? false;
  // Show onboarding until all 3 steps complete OR user has 3+ games; hide once any game is published
  const showOnboarding = completedSteps < 3 && totalGamesCount < 3 && !hasPublishedGame;
  // Show publish prompt when steps are done but no campaign has been published yet
  const showPublishPrompt = completedSteps === 3 && !hasPublishedGame && totalGamesCount < 3;
  const isNewUser = totalGamesCount === 0;
  // First draft (most recently edited) - used as a tangible "you made this" thumbnail in step 1
  const firstDraftCampaign = pickupGames[0] || contextCampaigns?.[0] || null;
  
  if (isLoading) {
    return <DashboardSkeleton />;
  }

  const OnboardingStep = ({ icon, title, description, completed, onClick, isClickable, isActive }) => (
    <button
      onClick={isClickable ? onClick : undefined}
      disabled={!isClickable}
      className={`p-4 sm:p-5 gch-surface rounded-[var(--admin-card-radius)] border transition-all duration-200 w-full text-left ${
        completed
          ? 'border-[var(--admin-accent)]/40'
          : isActive
          ? 'border-[var(--admin-accent)]/60 ring-1 ring-[var(--admin-accent)]/30'
          : 'opacity-70'
      } ${isClickable ? 'cursor-pointer hover:shadow-lg' : 'cursor-default'}`}
    >
      <div className="text-2xl mb-2 gch-text-primary">{completed ? '✓' : icon}</div>
      <h4 className={`text-sm font-semibold mb-1 ${completed ? 'text-[var(--admin-accent)]' : 'gch-text-primary'}`}>
        {title}
      </h4>
      <p className="text-xs gch-text-secondary">
        {description}
      </p>
      {isActive && !completed && (
        <div className="mt-3 text-xs font-medium text-[var(--admin-accent)] flex items-center gap-1">
          Start this step <span>→</span>
        </div>
      )}
    </button>
  );

  return (
    <div className="gch-content-width gch-page">
      <EmailVerificationBanner userProfile={userProfile} />
      {/* Success banner after purchasing credits */}
      {showCreditSuccessBanner && (
        <div className="gch-info-bar mb-4 !border-green-400/30 !bg-green-500/15">
          <span className="inline-flex items-center gap-2">
            <CheckCircleIcon className="w-4 h-4 text-green-400 shrink-0" />
            <span className="text-xs font-medium text-green-200">
              You're ready to publish! Open any draft campaign and hit Publish to go live.
            </span>
          </span>
          <button type="button" onClick={() => setShowCreditSuccessBanner(false)} className="gch-info-bar-action !text-green-300 hover:!text-white">
            Dismiss
          </button>
        </div>
      )}
      {showCreditCancelBanner && (
        <div className="gch-info-bar mb-4 !border-amber-400/30 !bg-amber-500/15">
          <span className="text-xs font-medium text-amber-200">
            Checkout cancelled. Your credits are unchanged.
          </span>
          <button type="button" onClick={() => setShowCreditCancelBanner(false)} className="gch-info-bar-action !text-amber-300 hover:!text-white">
            Dismiss
          </button>
        </div>
      )}

      {/* Success banner after upgrading to Agency */}
      {showUpgradedBanner && (
        <div className="mb-4 p-4 rounded-xl bg-green-500/15 border border-green-400/30 text-green-200">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-green-100">You've upgraded to Agency</p>
              <p className="text-sm mt-1 text-green-200/95">
                You now have access to unlimited workspaces and up to 10 team members.
                {upgradeChargeLabel
                  ? ` ${upgradeChargeLabel} has been charged to your payment method for the remainder of this billing period.`
                  : ' A prorated charge has been applied to your payment method.'}
              </p>
            </div>
            <button type="button" onClick={() => setShowUpgradedBanner(false)} className="text-green-300 hover:text-white shrink-0 font-medium text-sm">Dismiss</button>
          </div>
        </div>
      )}

      {/* Header - only show when NOT in onboarding */}
      {!showOnboarding && (
        <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight gch-text-primary">
              Dashboard
            </h1>
          </div>
          <button
            onClick={() => navigate('/studio/new')}
            className="btn-admin-primary btn-admin-md inline-flex items-center justify-center w-full sm:w-auto flex-shrink-0"
          >
            Create campaign
          </button>
        </div>
      )}

      {/* First-run hero - zero campaigns. One focused CTA that drops the
          user straight into the wizard (which now owns game-type selection
          itself, so we don't repeat type cards here). */}
      {showOnboarding && isNewUser && (
        <div className="mb-8 gch-card overflow-hidden relative">
          <div className="absolute inset-0 pointer-events-none">
            <ProceduralBackground rendererKey="skyClouds" isAnimating={true} style={{ opacity: 0.85 }} />
          </div>
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(100deg, rgba(15, 23, 42, 0.55) 0%, rgba(15, 23, 42, 0.28) 45%, rgba(15, 23, 42, 0.05) 75%, rgba(15, 23, 42, 0) 100%)',
            }}
          />
          <div className="relative p-6 sm:p-10 md:p-14">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl gch-heading tracking-tight leading-[1.05]"
              style={{ color: '#FFFFFF' }}
            >
              {userProfile?.firstName ? `Welcome, ${userProfile.firstName}.` : 'Welcome to SmilesFlow.'}
            </h2>
            <p className="mt-3 text-base sm:text-lg text-white/90 max-w-xl">
              You're about five minutes from a branded game with a shareable link and QR code. We'll walk you through it.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate('/studio/new')}
                className="btn-admin-primary btn-admin-md inline-flex items-center justify-center"
              >
                Create your first game
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Getting Started Section - Show until onboarding complete or 3+ games (only for users with at least one draft) */}
      {showOnboarding && !isNewUser && (() => {
        const draftId = firstDraftCampaign?.id;
        const draftTitle = firstDraftCampaign?.internalName || firstDraftCampaign?.options?.title || 'Untitled draft';
        const draftGameType = gameTypes.find((t) => t.id === firstDraftCampaign?.gameType);
        const draftColors = getGameTypeColors(firstDraftCampaign?.gameType);
        const activeStepIndex = !hasCreatedGame ? 0 : !hasCustomizedBranding ? 1 : !hasTestedGame ? 2 : 3;

        const steps = [
          {
            key: 'create',
            title: 'Create a game',
            desc: hasCreatedGame ? draftTitle : 'Pick a template and start a draft',
            time: '~3 min',
            done: hasCreatedGame,
            active: !hasCreatedGame,
            onClick: () => hasCreatedGame && draftId
              ? navigate(`/editor/${draftId}`)
              : navigate('/studio/new'),
          },
          {
            key: 'brand',
            title: 'Add your branding',
            desc: 'Logo, colours, and style',
            time: '~2 min',
            done: hasCustomizedBranding,
            active: hasCreatedGame && !hasCustomizedBranding,
            onClick: () => draftId && navigate(`/editor/${draftId}`),
          },
          {
            key: 'launch',
            title: 'Test & share',
            desc: 'Preview it, then publish to share',
            time: '~1 min',
            done: hasTestedGame,
            active: hasCreatedGame && hasCustomizedBranding && !hasTestedGame,
            onClick: () => draftId && window.open(`/p/${draftId}?preview=true`, '_blank'),
          },
        ];

        return (
        <>
        <div className="mb-8 gch-card overflow-hidden">
          {/* Welcome header */}
          <div className="p-6 sm:p-8 pb-0 sm:pb-0">
            <h2 className="text-2xl sm:text-3xl gch-heading tracking-tight">
              {businessProfile?.name
                ? 'Welcome to your new workspace'
                : userProfile?.firstName
                  ? `Welcome, ${userProfile.firstName}`
                  : 'Welcome to SmilesFlow'}
            </h2>
            <p className="mt-1 text-sm sm:text-base gch-text-secondary max-w-lg">
              {hasCreatedGame
                ? hasCustomizedBranding
                  ? "Almost there - test your game in preview, then publish to go live."
                  : "Great start! Now add your logo and colours to make it yours."
                : "Pick a template below or create from scratch. About 5 minutes."}
            </p>
          </div>

          {/* Progress + checklist */}
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold gch-text-secondary shrink-0">
                Step {Math.min(activeStepIndex + 1, 3)} of 3
              </span>
              <div className="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-[var(--admin-accent)] transition-all rounded-full"
                  style={{ width: `${onboardingProgress}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {steps.map((step, index) => {
                const isClickable = step.done || step.active;
                return (
                  <button
                    key={step.key}
                    type="button"
                    onClick={isClickable ? step.onClick : undefined}
                    disabled={!isClickable}
                    className={`w-full text-left rounded-[var(--admin-card-radius)] border px-4 py-3 transition-colors gch-surface ${
                      step.active
                        ? 'border-[var(--admin-accent)]/60 hover:bg-white/5'
                        : step.done
                          ? 'border-[var(--admin-accent)]/40 hover:bg-white/5'
                          : 'opacity-60'
                    } ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    <div className="flex items-center gap-3">
                      {step.key === 'create' && step.done && draftGameType ? (
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                          style={{ background: draftColors?.bg, border: `1px solid ${draftColors?.border}` }}
                          aria-hidden="true"
                        >
                          <draftGameType.icon className="w-4 h-4" style={{ color: draftColors?.icon || 'var(--admin-accent)' }} />
                        </div>
                      ) : (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          step.done ? 'bg-[var(--admin-accent)] text-[var(--admin-bg-surface)]' : step.active ? 'bg-[#E56A54] text-white' : 'bg-white/20 gch-text-secondary'
                        }`}>
                          {step.done ? '✓' : index + 1}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className={`text-sm font-semibold truncate ${step.done ? 'text-[var(--admin-accent)]' : step.active ? 'gch-text-primary' : 'gch-text-secondary'}`}>
                            {step.title}
                          </div>
                          {step.active && (
                            <span className="text-[11px] gch-text-muted shrink-0">{step.time}</span>
                          )}
                        </div>
                        <div className="text-xs gch-text-secondary truncate">{step.desc}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        </>
        );
      })()}

      {/* Ready to publish - all 3 steps done, but nothing published yet */}
      {showPublishPrompt && firstDraftCampaign?.id && (
        <div className="mb-8 gch-card overflow-hidden p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 gch-pill text-xs font-semibold px-3 py-1 mb-2">
              <CheckCircleIcon className="w-3.5 h-3.5" />
              Onboarding complete
            </div>
            <h2 className="text-2xl sm:text-3xl gch-heading tracking-tight">Ready to share?</h2>
            <p className="mt-1 text-sm sm:text-base gch-text-secondary max-w-lg">
              Publish your campaign to get a shareable link and QR code.
            </p>
          </div>
          <button
            onClick={() => navigate(`/studio/campaign/${firstDraftCampaign.id}`)}
            className="btn-admin-primary btn-admin-md inline-flex items-center justify-center shrink-0"
          >
            Publish Campaign
          </button>
        </div>
      )}

      {/* KPI strip + Published campaigns - only after onboarding. The old RECENT
          ACTIVITY chart was removed because the same data with more context lives
          on the campaign detail page; the dashboard's job is to direct action. */}
      {!showOnboarding && games.length > 0 && (
        <>
          {sinceLastVisit && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-emerald-500/[0.06] border border-emerald-400/20 flex items-center gap-3 flex-wrap">
              <span
                className="text-xs font-medium gch-text-secondary"
                title={`Counts every play, click and email captured from the start of ${sinceLastVisit.label} (your last dashboard visit) through to right now.`}
              >
                Since your last visit ({sinceLastVisit.label})
              </span>
              <span className="h-3 w-px bg-white/15" />
              {[
                ['plays', sinceLastVisit.plays],
                ['CTA clicks', sinceLastVisit.ctas],
                ['emails', sinceLastVisit.emails],
              ].filter(([, v]) => v > 0).map(([label, value]) => (
                <span key={label} className="text-xs font-semibold gch-text-primary tabular-nums">
                  +{value.toLocaleString()} <span className="font-normal gch-text-secondary">{label}</span>
                </span>
              ))}
            </div>
          )}
          <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Plays', recent: aggregateKpis.plays7d, lifetime: aggregateKpis.lifetimePlays, spark: aggregateKpis.playsSpark, color: '#9B59B6' },
              { label: 'Completions', recent: aggregateKpis.completions7d, lifetime: aggregateKpis.lifetimeCompletions, spark: aggregateKpis.completionsSpark, color: '#2ECC71' },
              { label: 'CTA clicks', recent: aggregateKpis.ctas7d, lifetime: aggregateKpis.lifetimeCtas, spark: aggregateKpis.ctasSpark, color: '#3498DB' },
              { label: 'Emails', recent: aggregateKpis.emails7d, lifetime: aggregateKpis.lifetimeEmails, spark: aggregateKpis.emailsSpark, color: '#F59E0B' },
            ].map(({ label, recent, lifetime, spark, color }) => {
              const hasSpark = (spark || []).some((v) => v > 0);
              const w = 56, h = 18;
              const max = hasSpark ? Math.max(...spark, 1) : 1;
              const step = spark && spark.length > 1 ? w / (spark.length - 1) : 0;
              const pts = (spark || []).map((v, i) => `${i * step},${h - (v / max) * h}`).join(' ');
              return (
                <div key={label} className="gch-card px-4 py-3.5 border border-white/10">
                  <p className="text-[10px] font-medium uppercase tracking-wider gch-text-muted">{label} · 7 days</p>
                  <div className="flex items-end justify-between gap-2 mt-1">
                    <p className="text-2xl font-bold tabular-nums gch-text-primary leading-none">
                      {(recent || 0).toLocaleString()}
                    </p>
                    {hasSpark && (
                      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="shrink-0" aria-hidden>
                        <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={pts} opacity="0.85" />
                      </svg>
                    )}
                  </div>
                  <p className="text-[11px] gch-text-muted mt-1.5 tabular-nums">
                    Lifetime {(lifetime || 0).toLocaleString()}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
              <h2 className="gch-heading-sm tracking-tight">Your campaigns</h2>
              {games.length > dashboardCampaignCards.length && (
                <button
                  type="button"
                  className="btn-admin-secondary btn-admin-sm inline-flex items-center gap-1.5 shrink-0"
                  onClick={() => navigate('/studio/campaigns')}
                >
                  <span className="text-xs font-semibold">Show all</span>
                </button>
              )}
            </div>
            <motion.div
              className="grid grid-cols-12 gap-3 sm:gap-6"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              {dashboardCampaignCards.map((campaign) => (
                <CampaignsCard key={campaign.id} campaign={campaign} />
              ))}
            </motion.div>
          </div>
        </>
      )}

    </div>
  );
}

export default Dashboard;
